import { TemplateVariable, VariableFormat } from '../types/communication';

/**
 * Extrai nomes de variáveis simples de {{ VAR }} para a autoria no editor (sincronizar a lista
 * declarada enquanto o usuário digita). Só reconhece a variável quando o token está FECHADO com
 * }} — caso contrário, cada caractere digitado dentro de {{ ... geraria uma variável parcial. A
 * extração robusta (laços, row.X) é feita no servidor na análise/salvamento.
 */
const extractTemplateNames = (source: string): string[] => {
    if (!source) return [];
    const names: string[] = [];
    const pattern = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:\|[^}]*)?\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(source)) !== null) {
        if (!names.includes(match[1])) names.push(match[1]);
    }
    return names;
};

const FORMAT_HINTS: [RegExp, VariableFormat][] = [
    [/E?MAIL/i, 'EMAIL'],
    [/PHONE|MOBILE|CELULAR|TELEFONE/i, 'PHONE'],
    [/DATE|_AT$|At$|DATA/i, 'DATE'],
    [/AMOUNT|VALUE|VALOR|PRICE|TOTAL_?AMOUNT/i, 'CURRENCY'],
    [/CNPJ|GOV_?TAX|DOCUMENT/i, 'GOV_TAX_ID'],
    [/CPF|SOCIAL_?ID/i, 'SOCIAL_ID'],
    [/URL|FILE|LINK|ADDRESS$/i, 'FILE_URL'],
    [/COUNT|NUMBER|QTD|TOTAL/i, 'NUMBER'],
];

export const guessFormat = (name: string): VariableFormat =>
    FORMAT_HINTS.find(([pattern]) => pattern.test(name))?.[1] ?? 'TEXT';

export const humanizeName = (name: string): string => {
    const spaced = name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_\-.]+/g, ' ')
        .trim()
        .toLocaleLowerCase('pt-BR');
    return spaced.charAt(0).toLocaleUpperCase('pt-BR') + spaced.slice(1);
};

export const newVariable = (name: string): TemplateVariable => ({
    name,
    label: humanizeName(name),
    format: guessFormat(name),
    required: true,
});

export const extractVariableNames = (...sources: string[]): string[] => {
    const names: string[] = [];
    sources.forEach((source) =>
        extractTemplateNames(source).forEach((name) => {
            if (!names.includes(name)) names.push(name);
        }),
    );
    return names;
};

export const syncVariables = (declared: TemplateVariable[], used: string[]): TemplateVariable[] => {
    const known = new Map(declared.map((v) => [v.name, v]));
    const ordered = used.map((name) => known.get(name) ?? newVariable(name));
    const unused = declared.filter((v) => !used.includes(v.name));
    return [...ordered, ...unused];
};

export const isValidVariableName = (name: string): boolean => /^[A-Za-z_][A-Za-z0-9_]*$/.test(name);

export const variableToken = (name: string): string => `{{${name}}}`;
